import { useUnit } from 'effector-react';
import { Dialog as RadixDialog } from 'radix-ui';
import { Group, Panel, Separator } from 'react-resizable-panels';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  $inspectorOpen,
  $sidebarOpen,
  inspectorToggled,
  sidebarToggled,
} from '../../entities/preferences';
import { COMPACT_QUERY, useMediaQuery } from '../../shared/lib/use-media-query';
import { ErrorBoundary, Button, EmptyState } from '../../shared/ui';
import { RotateCcw, TriangleAlert } from 'lucide-react';
import { Sidebar, type Page } from '../sidebar';
import { TopBar } from '../top-bar';

const separatorClass =
  'w-px bg-border-subtle transition-colors outline-none hover:bg-accent focus-visible:bg-accent data-[separator=active]:bg-accent';

export interface AppShellProps {
  page: Page | null;
  crumbs: readonly ReactNode[];
  /** Pages with an inspector pass its content (null when nothing is selected). */
  inspector?: ReactNode | null;
  onInspectorClose?: () => void;
  children: ReactNode;
}

export function AppShell({ page, crumbs, inspector, onInspectorClose, children }: AppShellProps) {
  const compact = useMediaQuery(COMPACT_QUERY);
  const [sidebarOpen, inspectorOpen, toggleSidebar, toggleInspector] = useUnit([
    $sidebarOpen,
    $inspectorOpen,
    sidebarToggled,
    inspectorToggled,
  ]);
  // The phone drawer starts closed, independent of the desktop sidebar preference.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const hasInspector = inspector !== undefined;
  const main = (
    <main className="h-full overflow-y-auto bg-bg-0">
      <ErrorBoundary fallback={(reset) => <PageCrashed onRetry={reset} />}>
        {children}
      </ErrorBoundary>
    </main>
  );

  if (compact) {
    return (
      <div className="flex h-full flex-col">
        <TopBar
          crumbs={crumbs}
          onToggleSidebar={() => {
            setDrawerOpen(true);
          }}
        />
        <div className="min-h-0 flex-1">{main}</div>
        <Drawer
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
          }}
        >
          <Sidebar
            current={page}
            onNavigate={() => {
              setDrawerOpen(false);
            }}
          />
        </Drawer>
        {hasInspector && inspector !== null && (
          <Sheet onClose={onInspectorClose ?? (() => undefined)}>{inspector}</Sheet>
        )}
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <TopBar
        crumbs={crumbs}
        onToggleSidebar={toggleSidebar}
        onToggleInspector={hasInspector ? toggleInspector : undefined}
      />
      <Group orientation="horizontal" className="min-h-0 flex-1">
        {sidebarOpen && (
          <>
            <Panel id="sidebar" defaultSize={240} minSize={200} maxSize={320}>
              <Sidebar current={page} />
            </Panel>
            <Separator className={separatorClass} />
          </>
        )}
        <Panel id="main" minSize={420}>
          {main}
        </Panel>
        {hasInspector && inspectorOpen && inspector !== null && (
          <>
            <Separator className={separatorClass} />
            <Panel id="inspector" defaultSize={360} minSize={320} maxSize={560}>
              <aside className="h-full overflow-y-auto bg-bg-1">{inspector}</aside>
            </Panel>
          </>
        )}
      </Group>
    </div>
  );
}

function PageCrashed({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <EmptyState
      className="py-24"
      icon={<TriangleAlert />}
      title={t('errors.pageFailed')}
      body={t('errors.pageFailedBody')}
      action={
        <Button leadingIcon={<RotateCcw />} onClick={onRetry}>
          {t('errors.tryAgain')}
        </Button>
      }
    />
  );
}

function Drawer({
  open,
  onClose,
  children,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <RadixDialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-40 animate-fade-in bg-overlay" />
        <RadixDialog.Content
          aria-describedby={undefined}
          className="fixed inset-y-0 left-0 z-50 w-60 animate-fade-in border-r border-border-subtle bg-bg-1 shadow-popover outline-none"
        >
          <RadixDialog.Title className="sr-only">{t('nav.label')}</RadixDialog.Title>
          {children}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

function Sheet({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <RadixDialog.Root
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <RadixDialog.Portal>
        <RadixDialog.Content
          aria-describedby={undefined}
          // Focus the sheet itself: focusing its first button would pop that button's tooltip.
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            (event.currentTarget as HTMLElement | null)?.focus();
          }}
          className="fixed inset-0 z-50 animate-fade-in overflow-y-auto bg-bg-1 outline-none"
        >
          <RadixDialog.Title className="sr-only">{t('shell.details')}</RadixDialog.Title>
          {children}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
